import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiError } from "../lib/api";
import { Printer, ArrowLeft, Package } from "@phosphor-icons/react";
import { PUBLIC_FRONT_URL } from "../lib/publicFront";

const FRONTEND_BASE = PUBLIC_FRONT_URL;

// ---------------------------------------------------------------------------
// PVC card press spec (matches printer's Sample A / Sample B):
//   - Trim size:       90 × 50 mm
//   - Bleed:           3 mm on all sides → printed canvas 96 × 56 mm
//   - Cut radius:      2.5 mm (standard credit card die)
//   - Inks:            2 spot colours only — pure black #000 + pure yellow #FFCC00.
//                      Greys, drop shadows and gradients are forbidden.
//   - Cut line:        0.25 pt black hairline rounded-rectangle drawn at the
//                      90×50 mm trim edge — the cutter's reference path.
//   - Variants:        A → QR sits inside a sharp white box (max scan contrast).
//                      B → QR sits directly on yellow (cleanest 2-colour look).
// One card per PDF page. Printer arranges imposition themselves.
// ---------------------------------------------------------------------------

const YELLOW = "#FFCC00";
const BLACK  = "#000000";
const WHITE  = "#FFFFFF";
const CARD_W = 96;   // mm — bleed-to-bleed page width
const CARD_H = 56;   // mm — bleed-to-bleed page height
const BLEED  = 3;    // mm — extra each side
const TRIM_R = 2.5;  // mm — die-cut corner radius

export default function PrinterSheet() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const idsParam     = (params.get("ids") || "").split(",").filter(Boolean);
  const statusFilter = params.get("status") || "unassigned";
  const batchFilter  = params.get("batch") || "";
  const limit        = parseInt(params.get("limit") || "0", 10) || 0;
  const offset       = parseInt(params.get("offset") || "0", 10) || 0;
  const variantRaw = (params.get("variant") || "bw").toLowerCase();
  const variant = ["a", "b", "bw"].includes(variantRaw) ? variantRaw : "bw";

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/codes");
        if (!alive) return;
        const all = data.codes || [];
        let filtered;
        if (idsParam.length) {
          filtered = all.filter((c) => idsParam.includes(c.id));
        } else if (statusFilter === "all") {
          filtered = all;
        } else {
          filtered = all.filter((c) => c.status === statusFilter);
        }
        if (batchFilter) filtered = filtered.filter((c) => c.batch_name === batchFilter);
        if (offset || limit) {
          filtered = filtered.slice(offset, limit ? offset + limit : undefined);
        }
        setCodes(filtered);
      } catch (e) {
        if (!alive) return;
        if (e?.response?.status === 401) navigate("/admin/login");
        else setErr(formatApiError(e));
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line

  const setVariant = (v) => {
    const p = new URLSearchParams(params);
    p.set("variant", v);
    setParams(p, { replace: true });
  };

  if (loading) return <div className="p-8 label-tag">Loading codes…</div>;
  if (err) return (
    <div className="p-8 max-w-md mx-auto">
      <Link to="/admin/dashboard" className="label-tag inline-flex items-center gap-2"><ArrowLeft size={14} weight="bold" /> Back</Link>
      <div className="mt-4 bg-red-50 border border-red-300 p-4 label-tag text-red-700">{err}</div>
    </div>
  );

  return (
    <div className="bg-neutral-200 min-h-screen print:bg-white">
      <div className="no-print sticky top-0 bg-white border-b border-black z-10" data-testid="printer-toolbar">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <Link to="/admin/dashboard" className="label-tag inline-flex items-center gap-2" data-testid="printer-back">
            <ArrowLeft size={14} weight="bold" /> Back to dashboard
          </Link>

          {/* Variant toggle: BW is the new production default. A/B (yellow) kept for legacy/reference. */}
          <div className="inline-flex items-stretch border-2 border-black" data-testid="variant-toggle">
            <button
              type="button"
              onClick={() => setVariant("bw")}
              className={`label-tag px-4 py-2 brutal-btn ${variant === "bw" ? "bg-black text-white" : "bg-white"}`}
              data-testid="variant-bw-btn"
            >
              Black & White
            </button>
            <button
              type="button"
              onClick={() => setVariant("a")}
              className={`label-tag px-4 py-2 brutal-btn border-l-2 border-black ${variant === "a" ? "bg-[color:var(--cc-yellow)]" : "bg-white"}`}
              data-testid="variant-a-btn"
            >
              Yellow A (legacy)
            </button>
            <button
              type="button"
              onClick={() => setVariant("b")}
              className={`label-tag px-4 py-2 brutal-btn border-l-2 border-black ${variant === "b" ? "bg-[color:var(--cc-yellow)]" : "bg-white"}`}
              data-testid="variant-b-btn"
            >
              Yellow B (legacy)
            </button>
          </div>

          <div className="label-tag text-black inline-flex items-center gap-2">
            <Package size={14} weight="bold" />
            {codes.length} cards · 96×56 mm each
          </div>

          <button onClick={() => window.print()} className="bg-[color:var(--cc-yellow)] label-tag px-5 py-3 border-2 border-black brutal-btn flex items-center gap-2" data-testid="printer-print-btn">
            <Printer size={16} weight="bold" /> Save as PDF
          </button>
        </div>
        <div className="max-w-4xl mx-auto px-6 pb-3 text-xs text-black leading-relaxed">
          <strong>Press-ready spec:</strong> 90×50 mm trim · 3 mm bleed (canvas 96×56 mm) · 2.5 mm corner radius · hairline black through-cut line at trim edge.
          <br />
          {variant === "bw" ? (
            <>Black & white: 1 ink — pure <span style={{background: "#000", color: "#fff", padding: "0 4px"}}>BLACK</span> on white substrate. <strong>Current production default — first 500 cards used this.</strong></>
          ) : (
            <>Yellow (legacy): 2 spot colours — pure <span style={{background: "#000", color: YELLOW, padding: "0 4px"}}>BLACK</span> + pure <span style={{background: YELLOW, color: "#000", padding: "0 4px"}}>YELLOW</span>. Parked.</>
          )}
          <br />
          When the print dialog opens: <em>Destination → Save as PDF</em>, <em>Paper size → 96×56 mm (custom)</em>, <em>Margins → None</em>, <em>Scale → 100%</em>, <em>Background graphics → ON</em>.
        </div>
      </div>

      <div className="py-6 print:py-0">
        {codes.map((c) => (
          <PrinterCard key={c.id} code={c.code} batch={c.batch_name || ""} variant={variant} />
        ))}
        {codes.length === 0 && (
          <div className="max-w-md mx-auto p-8 text-center label-tag text-black">
            No codes match this filter.
          </div>
        )}
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: ${CARD_W}mm ${CARD_H}mm; margin: 0; }
          html, body { background: white; margin: 0; padding: 0; }
          .printer-card {
            margin: 0 !important;
            box-shadow: none !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
}

function PrinterCard({ code, batch, variant }) {
  const isB = variant === "b";
  const isBW = variant === "bw";
  // BW: white background, black artwork, no yellow ink at all (cheapest press, first 500 cards used this).
  // A:  yellow background, white QR box (legacy).
  // B:  yellow background, QR directly on yellow (legacy).
  const bg = isBW ? WHITE : YELLOW;
  const brandBg = BLACK;
  const brandFg = isBW ? WHITE : YELLOW;
  const qrBg = isBW ? WHITE : (isB ? YELLOW : WHITE);
  return (
    <div
      className="printer-card mx-auto"
      style={{
        width:  `${CARD_W}mm`,
        height: `${CARD_H}mm`,
        background: bg,
        boxSizing: "border-box",
        pageBreakAfter: "always",
        breakAfter: "page",
        marginBottom: "8mm",
        position: "relative",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.08)", // screen-only hint — does NOT print
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
        colorAdjust: "exact",
      }}
      data-testid={`printer-card-${code}`}
    >
      {/* Through-cut line — 0.25pt black hairline at the 90×50 trim edge. */}
      <div
        style={{
          position: "absolute",
          left:   `${BLEED}mm`,
          top:    `${BLEED}mm`,
          width:  `${CARD_W - 2 * BLEED}mm`,
          height: `${CARD_H - 2 * BLEED}mm`,
          border: `0.25pt solid ${BLACK}`,
          borderRadius: `${TRIM_R}mm`,
          pointerEvents: "none",
        }}
        aria-hidden
      />

      {/* Safe zone — 2 mm inside the cut line (= 5 mm from bleed edge). */}
      <div
        style={{
          position: "absolute",
          left:    `${BLEED + 2}mm`,
          top:     `${BLEED + 2}mm`,
          right:   `${BLEED + 2}mm`,
          bottom:  `${BLEED + 2}mm`,
          display: "flex",
          alignItems: "stretch",
          gap: "3mm",
        }}
      >
        {/* QR — left, centred vertically. Variant A: white box. Variant B: yellow. */}
        <div
          style={{
            width:  "42mm",
            height: "42mm",
            flexShrink: 0,
            alignSelf: "center",
            background: qrBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <QRCodeSVG
            value={`${FRONTEND_BASE}/c/${code}`}
            size={158}                /* ≈ 42 mm at 96 dpi · SVG so scales losslessly */
            level="H"
            bgColor={qrBg}
            fgColor={BLACK}
            includeMargin={true}      /* built-in 4-module quiet zone */
          />
        </div>

        {/* Right column — brand pill, code, instruction. All text pure #000. */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            paddingTop: "1mm",
            paddingBottom: "1mm",
            color: BLACK,
          }}
        >
          <div>
            <div
              style={{
                display: "inline-block",
                background: brandBg,
                color: brandFg,
                padding: "1.1mm 2mm",
                fontFamily: "'Cabinet Grotesk', sans-serif",
                fontWeight: 900,
                fontSize: "11pt",
                letterSpacing: "-0.01em",
                lineHeight: 1,
              }}
            >
              CURB CREW
            </div>
            <div
              style={{
                fontSize: "6pt",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                marginTop: "1.5mm",
                color: BLACK,
                fontWeight: 700,
              }}
            >
              Smart business card · ZA
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: "5pt",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: BLACK,
                fontWeight: 700,
              }}
            >
              Card code
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "14pt",
                fontWeight: 800,
                letterSpacing: "0.04em",
                lineHeight: 1,
                marginTop: "0.6mm",
                color: BLACK,
              }}
            >
              {code}
            </div>
          </div>

          <div>
            <div style={{ fontSize: "6pt", fontWeight: 700, lineHeight: 1.15, color: BLACK }}>
              ← Scan QR · no app
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "5pt",
                marginTop: "0.6mm",
                color: BLACK,
              }}
            >
              curbcrew.co.za/c/{code}
            </div>
            {batch && (
              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "4pt",
                  marginTop: "0.5mm",
                  color: BLACK,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
                title={batch}
                data-testid={`card-batch-${code}`}
              >
                {batch}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
